import {
  BrowserCacheLocation,
  InteractionType,
  LogLevel,
  PublicClientApplication
} from '@azure/msal-browser';
import type { MsalGuardConfiguration, MsalInterceptorConfiguration } from '@azure/msal-angular';
import { environment } from '../../../../environments/environment';

const MSAL_RUNTIME_CONFIG_KEY = 'adminMsalRuntimeConfig';

interface MsalRuntimeConfig {
  clientId: string;
  tenantId: string;
}

function normalizeOptionalString(value?: string): string | undefined {
  const normalized = value?.trim();
  return normalized !== undefined && normalized.length > 0 ? normalized : undefined;
}

function getSessionStorage(): Storage | undefined {
  try {
    return globalThis.sessionStorage;
  } catch {
    return undefined;
  }
}

function readRuntimeConfigFromStorage(): Partial<MsalRuntimeConfig> {
  const storage = getSessionStorage();

  if (storage === undefined) {
    return {};
  }

  const raw = storage.getItem(MSAL_RUNTIME_CONFIG_KEY);

  if (raw === null) {
    return {};
  }

  try {
    const parsed = JSON.parse(raw) as Partial<MsalRuntimeConfig>;

    return {
      clientId: normalizeOptionalString(parsed.clientId),
      tenantId: normalizeOptionalString(parsed.tenantId),
    };
  } catch {
    storage.removeItem(MSAL_RUNTIME_CONFIG_KEY);
    return {};
  }
}

/**
 * Resolves the active MSAL app registration config using runtime overrides first,
 * then current in-memory values, and finally environment defaults.
 */
function buildMsalRuntimeConfig(overrides?: Partial<MsalRuntimeConfig>): MsalRuntimeConfig {
  return {
    clientId: normalizeOptionalString(overrides?.clientId)
      ?? normalizeOptionalString(msalConfig.clientId)
      ?? environment.msalClientId,
    tenantId: normalizeOptionalString(overrides?.tenantId)
      ?? normalizeOptionalString(msalConfig.tenantId)
      ?? environment.msalTenantId,
  };
}

export const msalConfig = {
  clientId: environment.msalClientId,
  tenantId: environment.msalTenantId,
  postLogoutRedirectUri: environment.msalPostLogoutRedirectUri,
};

const runtimeConfig = readRuntimeConfigFromStorage();

if (runtimeConfig.clientId !== undefined) {
  msalConfig.clientId = runtimeConfig.clientId;
}

if (runtimeConfig.tenantId !== undefined) {
  msalConfig.tenantId = runtimeConfig.tenantId;
}

export function setMsalRuntimeConfig(overrides: Partial<MsalRuntimeConfig>): MsalRuntimeConfig {
  // Persist backend-provided values so redirect round-trips keep the same app/tenant.
  const resolvedConfig = buildMsalRuntimeConfig(overrides);
  msalConfig.clientId = resolvedConfig.clientId;
  msalConfig.tenantId = resolvedConfig.tenantId;

  const storage = getSessionStorage();

  if (storage !== undefined) {
    storage.setItem(MSAL_RUNTIME_CONFIG_KEY, JSON.stringify(resolvedConfig));
  }

  return resolvedConfig;
}

export function clearMsalRuntimeConfig(): void {
  // Revert to static environment configuration and clear session-scoped overrides.
  msalConfig.clientId = environment.msalClientId;
  msalConfig.tenantId = environment.msalTenantId;
  getSessionStorage()?.removeItem(MSAL_RUNTIME_CONFIG_KEY);
}

/**
 * Creates a PublicClientApplication from the resolved runtime config.
 * This enables per-organization clientId/tenantId without rebuilding the app.
 */
export function createMsalInstance(
  overrides?: Partial<MsalRuntimeConfig>
): PublicClientApplication {
  const resolvedConfig = buildMsalRuntimeConfig(overrides);
  const origin = globalThis.location?.origin;
  const redirectUri = origin !== undefined && origin !== null
    ? `${origin}/auth/callback`
    : '/auth/callback';

  return new PublicClientApplication({
    auth: {
      clientId: resolvedConfig.clientId,
      authority: `https://login.microsoftonline.com/${resolvedConfig.tenantId}`,
      redirectUri: redirectUri,
      postLogoutRedirectUri: msalConfig.postLogoutRedirectUri,
    },
    cache: { cacheLocation: BrowserCacheLocation.SessionStorage },
    system: {
      loggerOptions: {
        logLevel: LogLevel.Warning,
        piiLoggingEnabled: false,
      },
    },
  });
}

export const msalInstance = createMsalInstance();

// scopes requested on login
export const loginRequest = { scopes: ['openid', 'profile', 'email', 'User.Read'] };

// Optional - only needed to protect routes via MSAL Guard
export const msalGuardConfig: MsalGuardConfiguration = {
  interactionType: InteractionType.Redirect,
  authRequest: loginRequest,
};

// Used by MSAL Interceptor to attach bearer tokens to outbound API calls
export const msalInterceptorConfig: MsalInterceptorConfiguration = {
  interactionType: InteractionType.Redirect,
  protectedResourceMap: new Map([
    ['https://graph.microsoft.com/v1.0/me', ['User.Read']],
  ]),
};
